import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { useApolloClient, useMutation } from '@apollo/client';

import { userShape, paymentsShape } from 'constants/propTypes';
import { REVERT_CHANGE, CANCEL_PAYMENTS, ROLLBACK_VOUCHER_REDEMPTION, ADD_NOTE_TO_BOOKING, CONFIRM_ON_HOLD } from 'store/mutations';
import UPDATE_VOUCHER from 'pages/VoucherDetails/mutations/UPDATE_VOUCHER';
import Toast from 'components/ui/Toast';
import AlertBox from 'components/ui/AlertBox';
import { rollbackVoucherPayments } from 'components/ui/Payments/Payments.utils';
import { getBookingJourneysURL } from 'utils/urls';
import { StyledRefresh, StyledCancelButton } from './RevertExchangeButton.styles';
import { GET_CUSTOMER_DETAILS, GET_VOUCHER_ALIAS } from 'store/queries';
import { codes } from 'constants/payment';
import { bookingStatus } from 'types/booking';

const RevertExchangeButton = ({
  reference,
  payments,
  currency,
  hasHotel,
  history: { push, location },
  user: { paymentLocation },
  shouldRedirectToClubEurostar = false,
  ctaText,
  status,
  changeExpiryTimestamp
}) => {
  const client = useApolloClient();

  const [freeUpgradeLoading, setFreeUpgradeLoading] = useState(false);
  const bookingJourneysURL = getBookingJourneysURL(reference);
  const membershipNumber = new URLSearchParams(location?.search).get('membershipNumber');
  const isFreeUpgrade = new URLSearchParams(location?.search).get('isFreeUpgrade') === 'true';
  const addPassengers = new URLSearchParams(location?.search).get('addPassengers');
  const membershipNumberUrl = membershipNumber ? `?membershipNumber=${membershipNumber}` : '';
  const redirectToJourneys = async () => {
    if (status === bookingStatus.TEMPORARILY_ON_HOLD && changeExpiryTimestamp && addPassengers) {
      await confirmOnHold();
    }
    push(`${bookingJourneysURL}${membershipNumberUrl}`);
  };
  const redirectToClubEurostar = () =>
    push(`/clubeurostar/manage`, {
      prevPath: location.pathname,
      prevSearch: location.search,
      prevHash: location.hash
    });

  const [revertChange, { loading: revertChangeLoading, error }] = useMutation(REVERT_CHANGE, {
    variables: { bookingReference: reference },
    update: redirectToJourneys
  });

  const [updateVoucher] = useMutation(UPDATE_VOUCHER, { fetchPolicy: 'no-cache' });
  const [addNote] = useMutation(ADD_NOTE_TO_BOOKING, { fetchPolicy: 'no-cache' });
  const [cancelPaymentsMutation, { loading: cancelPaymentsLoading, error: cancelPaymentsError }] = useMutation(CANCEL_PAYMENTS, {
    fetchPolicy: 'no-cache'
  });

  const [rollbackVoucherRedemption, { loading: rollbackVoucherRedemptionLoading, error: rollbackVoucherRedemptionError }] = useMutation(
    ROLLBACK_VOUCHER_REDEMPTION,
    { fetchPolicy: 'no-cache' }
  );

  const cancelPayments = (customerId) =>
    Promise.all([
      cancelPaymentsMutation({
        variables: {
          bookingReference: reference,
          paymentLocation: paymentLocation.name,
          currency,
          hasHotel,
          customerId,
          shouldCancelLoyalty: true
        }
      }),
      rollbackVoucherPayments({
        payments,
        bookingReference: reference,
        currency,
        rollbackMutation: rollbackVoucherRedemption
      })
    ]);

  const [confirmOnHold] = useMutation(CONFIRM_ON_HOLD, {
    variables: { bookingReference: reference }
  });

  const onClick = async () => {
    let customerInfo;

    if (membershipNumber) {
      try {
        const result = await client.query({
          query: GET_CUSTOMER_DETAILS,
          variables: {
            membershipNumber
          }
        });
        customerInfo = result?.data?.getCustomerDetails;
      } catch (e) {
        console.error('Failed to get customer details:', e);
      }
    }

    const voucherAliasRef = payments.find(({ method, paymentStatus }) => method === codes.VOUCHER && paymentStatus === 'P')?.reference;
    await cancelPayments(customerInfo?.profile?.customerId);

    if (isFreeUpgrade) {
      if (voucherAliasRef) {
        setFreeUpgradeLoading(true);

        try {
          await client
            .query({
              query: GET_VOUCHER_ALIAS,
              variables: {
                voucherCodeLastChars: voucherAliasRef,
                bookingReference: reference
              }
            })
            .then(async (response) => {
              if (response?.data?.getVoucherAliasByLastCharsAndBookingReference) {
                await updateVoucher({
                  variables: {
                    voucherAlias: response?.data?.getVoucherAliasByLastCharsAndBookingReference,
                    reason: 'Free upgrade cancelled',
                    active: false
                  }
                });

                await addNote({
                  variables: {
                    bookingReference: reference,
                    note: `Free upgrade reverted for discount code ${response?.data?.getVoucherAliasByLastCharsAndBookingReference}`
                  }
                });
              }
            });
        } catch (err) {
          console.error('Failed to update vouchers:', err);
        } finally {
          setFreeUpgradeLoading(false);
        }
      }
    }

    await revertChange();
    return shouldRedirectToClubEurostar ? redirectToClubEurostar() : redirectToJourneys();
  };

  const loading = revertChangeLoading || cancelPaymentsLoading || rollbackVoucherRedemptionLoading || freeUpgradeLoading;

  return (
    <Toast>
      {({ createToast }) => {
        if (error) {
          redirectToJourneys();
          createToast(<FormattedMessage id="exchanges.revertToast" />);
        }

        return (
          <>
            {(cancelPaymentsError || rollbackVoucherRedemptionError) && (
              <AlertBox variant={AlertBox.variants.ERROR}>
                <FormattedMessage id="exchangeConfirmation.cancelPaymentsFailure" />
              </AlertBox>
            )}
            <StyledCancelButton
              loading={loading}
              disabled={loading}
              onClick={onClick}
              data-testid="abandon-cancellation"
              data-cy="abandon-cancellation"
            >
              <StyledRefresh size="18" />
              {shouldRedirectToClubEurostar ? (
                <FormattedMessage id={ctaText || 'exchangeConfirmation.restoreBookingClubEurostar'} />
              ) : (
                <FormattedMessage id={ctaText || 'exchangeConfirmation.restoreOriginalBooking'} />
              )}
            </StyledCancelButton>
          </>
        );
      }}
    </Toast>
  );
};

RevertExchangeButton.propTypes = {
  reference: PropTypes.string,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    location: PropTypes.shape({
      search: PropTypes.string,
      pathname: PropTypes.string,
      hash: PropTypes.string
    })
  }),
  user: userShape,
  payments: paymentsShape,
  currency: PropTypes.string,
  hasHotel: PropTypes.bool,
  shouldRedirectToClubEurostar: PropTypes.bool,
  ctaText: PropTypes.string,
  status: PropTypes.string,
  changeExpiryTimestamp: PropTypes.string
};

export default RevertExchangeButton;
